/**
 * @file 채널 API
 * 채널 관리, 멤버 관리, 피드·댓글·파일·업무(프로젝트)·일정·투표 등
 * 채널 내 모든 협업 기능을 처리합니다.
 */

import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * 폴더 범위 → 쿼리 파라미터.
 *
 * <p>서버는 파라미터 두 개로 세 가지 보기를 구분한다 —
 * 전체(둘 다 없음) / 특정 폴더({@code folderId}) / 미분류({@code onlyUnfiled=true}).
 * "폴더 지정 없음(전체)" 과 "폴더가 빈 것만(미분류)" 은 뜻이 정반대인데 folderId 하나로는
 * 둘 다 빈 값이 되어 구분할 수 없어서다.
 *
 * <p>빈 값을 아예 <b>보내지 않는다</b> — `folderId=` 같은 빈 문자열이 가면 Spring 이 Long 변환에
 * 실패해 400 이 된다.
 */
function folderParams({ folderId, onlyUnfiled } = {}) {
    const out = {};
    if (folderId != null) out.folderId = folderId;
    if (onlyUnfiled) out.onlyUnfiled = true;
    return out;
}

export const channelApi = {
    // 채널
    /** 채널 목록 조회 */
    list:         ()                  => api.get(`${API_BASE}/channel/list`),

    /** 채널 생성
     * @param {object} data - 채널 생성 데이터
     */
    create:       (data)              => api.post(`${API_BASE}/channel/create`, data),

    /** 채널 정보 수정
     * @param {number} channelId - 채널 ID
     * @param {object} data - 수정할 채널 데이터
     */
    update:       (channelId, data)   => api.put(`${API_BASE}/channel/${channelId}`, data),

    /** 채널 상세 조회
     * @param {number} channelId - 채널 ID
     */
    get:          (channelId)         => api.get(`${API_BASE}/channel/${channelId}`),

    /** 채널 삭제
     * @param {number} channelId - 채널 ID
     */
    delete:       (channelId)         => api.delete(`${API_BASE}/channel/${channelId}`),

    /** 현재 사용자의 시스템 관리자 여부 확인 */
    isSysAdmin:   ()                  => api.get(`${API_BASE}/channel/is-sys-admin`),

    /** 피드 좋아요 토글 — { liked, likeCount } 반환 */
    toggleFeedLike: (feedId)          => api.post(`${API_BASE}/channel/feed/${feedId}/like`, {}),

    // 멤버
    /** 채널 멤버 목록 조회
     * @param {number} channelId - 채널 ID
     */
    getMembers:     (channelId)               => api.get(`${API_BASE}/channel/${channelId}/members`),

    /** 채널에 멤버 단일 추가
     * @param {number} channelId - 채널 ID
     * @param {number} employeeId - 추가할 직원 ID
     */
    addMember:      (channelId, employeeId)   => api.post(`${API_BASE}/channel/${channelId}/member/${employeeId}`, {}),

    /** 채널에 멤버 일괄 추가
     * @param {number} channelId - 채널 ID
     * @param {number[]} employeeIds - 추가할 직원 ID 배열
     */
    addMembers:     (channelId, employeeIds)  => api.post(`${API_BASE}/channel/${channelId}/members`, employeeIds),

    /** 채널 멤버 제거
     * @param {number} channelId - 채널 ID
     * @param {number} employeeId - 제거할 직원 ID
     */
    removeMember:   (channelId, employeeId)   => api.delete(`${API_BASE}/channel/${channelId}/member/${employeeId}`),

    /** 현재 사용자의 채널 관리자 여부 확인
     * @param {number} channelId - 채널 ID
     */
    isChannelAdmin: (channelId)               => api.get(`${API_BASE}/channel/${channelId}/is-admin`),

    // 폴더 — 채널 안을 부서·섹션 단위로 나누는 구획
    /** 채널 폴더 목록 조회 — { folders, unfiledFeedCount, canManage } */
    getFolders:   (channelId)          => api.get(`${API_BASE}/channel/${channelId}/folders`),

    /** 폴더 생성 (PM·채널 관리자)
     * @param {number} channelId
     * @param {object} data - { name, description?, sortOrder?, memberIds? }
     */
    createFolder: (channelId, data)    => api.post(`${API_BASE}/channel/${channelId}/folders`, data),

    /** 폴더 수정 (PM·채널 관리자) — 참가자는 별도 API 로 관리한다 */
    updateFolder: (folderId, data)     => api.put(`${API_BASE}/channel/folders/${folderId}`, data),

    /** 폴더 삭제 (PM·채널 관리자)
     *
     *  안의 피드·일정·업무는 삭제되지 않고 targetFolderId 로 옮겨진다(미지정 시 미분류).
     *  @param {number} folderId
     *  @param {number} [targetFolderId] 옮길 대상 폴더. 생략하면 미분류
     */
    deleteFolder: (folderId, targetFolderId) =>
        api.delete(`${API_BASE}/channel/folders/${folderId}`,
            targetFolderId != null ? { params: { targetFolderId } } : undefined),

    /** 폴더 참가자 목록 (PM·채널 관리자) */
    getFolderMembers:    (folderId)  => api.get(`${API_BASE}/channel/folders/${folderId}/members`),

    /** 폴더 참가자 추가 (PM·채널 관리자) — 채널 참가자 중에서만 가능
     * @param {number} folderId
     * @param {number[]} employeeIds
     */
    addFolderMembers:    (folderId, employeeIds) =>
        api.post(`${API_BASE}/channel/folders/${folderId}/members`, employeeIds),

    /** 폴더 참가자 제거 (PM·채널 관리자) */
    removeFolderMember:  (folderId, employeeId) =>
        api.delete(`${API_BASE}/channel/folders/${folderId}/members/${employeeId}`),

    /** 폴더 순서 변경 (채널 관리자) — 배열 순서대로 sortOrder 재부여 */
    reorderFolders: (channelId, orderedIds) =>
        api.put(`${API_BASE}/channel/${channelId}/folders/reorder`, orderedIds),

    // 피드
    /** 채널 피드 목록 조회 (페이징)
     * @param {number} channelId - 채널 ID
     * @param {number} [page=0] - 페이지 번호
     * @param {number} [size=10] - 페이지 크기
     * @param {object} [folderScope] - 폴더 범위 { folderId?, onlyUnfiled? }.
     *   생략(전체) / { folderId } (그 폴더) / { onlyUnfiled: true } (미분류) 세 가지.
     */
    getFeeds:   (channelId, page = 0, size = 10, folderScope = {}) =>
        api.get(`${API_BASE}/channel/${channelId}/feeds`, { page, size, ...folderParams(folderScope) }),

    /** 피드 폴더 이동 — folderId 가 null 이면 미분류로 뺀다 */
    moveFeedToFolder: (feedId, folderId) =>
        api.patch(`${API_BASE}/channel/feed/${feedId}/folder`, { folderId: folderId ?? null }),

    /** 단건 피드 조회 (알람/deep link 진입용) */
    getFeed:    (feedId)            => api.get(`${API_BASE}/channel/feed/${feedId}`),

    /** 피드 고정/고정 해제
     * @param {number} feedId - 피드 ID
     */
    pinFeed:    (feedId)            => api.post(`${API_BASE}/channel/feed/${feedId}/pin`, {}),

    /** 피드 작성
     * @param {number} channelId - 채널 ID
     * @param {object} data - 피드 내용 데이터
     */
    createFeed: (channelId, data)   => api.post(`${API_BASE}/channel/${channelId}/feed`, data),

    /** 피드 수정
     * @param {number} feedId - 피드 ID
     * @param {object} data - 수정할 피드 데이터
     */
    updateFeed: (feedId, data)      => api.put(`${API_BASE}/channel/feed/${feedId}`, data),

    /** 피드 삭제
     * @param {number} feedId - 피드 ID
     */
    deleteFeed: (feedId)            => api.delete(`${API_BASE}/channel/feed/${feedId}`),

    // 댓글
    /** 댓글 작성
     * @param {number} feedId - 피드 ID
     * @param {object} data - 댓글 내용 데이터
     */
    createComment: (feedId, data)    => api.post(`${API_BASE}/channel/feed/${feedId}/comment`, data),

    /** 댓글 수정
     * @param {number} commentId - 댓글 ID
     * @param {object} data - 수정할 댓글 데이터
     */
    updateComment: (commentId, data) => api.put(`${API_BASE}/channel/comment/${commentId}`, data),

    /** 댓글 삭제
     * @param {number} commentId - 댓글 ID
     */
    deleteComment: (commentId)       => api.delete(`${API_BASE}/channel/comment/${commentId}`),

    // 파일
    /** 첨부파일 삭제
     * @param {number} attachmentId - 첨부파일 ID
     */
    deleteAttachment:  (attachmentId)  => api.delete(`${API_BASE}/channel/attachment/${attachmentId}`),

    /** 첨부파일 미리보기 (blob). PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환. */
    previewAttachment: (attachmentId)  => api.download(`${API_BASE}/channel/attachment/${attachmentId}/preview`),

    /** 채널 파일 목록 조회
     * @param {number} channelId - 채널 ID
     * @param {number} [uploaderId] - 특정 업로더로 필터링할 직원 ID (생략 시 전체 조회)
     */
    getFiles:          (channelId, uploaderId, folderScope = {}) =>
        api.get(`${API_BASE}/channel/${channelId}/files`, {
            ...(uploaderId ? { uploaderId } : {}),
            ...folderParams(folderScope),
        }),

    /** 채널 파일 업로드 (multipart/form-data)
     * @param {number} channelId - 채널 ID
     * @param {File} file - 업로드할 파일
     * @param {number} [feedId] - 연결할 피드 ID
     * @param {number} [commentId] - 연결할 댓글 ID
     * @param {number} [taskId] - 연결할 업무 ID
     */
    uploadFile: (channelId, file, feedId, commentId, taskId) => {
        const form = new FormData();
        form.append("file", file);
        if (feedId)    form.append("feedId", feedId);
        if (commentId) form.append("commentId", commentId);
        if (taskId)    form.append("taskId", taskId);
        return api.upload(`${API_BASE}/channel/${channelId}/upload`, form);
    },

    /** 업무에 첨부된 파일 목록 조회
     * @param {number} taskId - 업무 ID
     */
    getTaskAttachments: (taskId) => api.get(`${API_BASE}/channel/task/${taskId}/attachments`),

    // 업무 (프로젝트)
    /** 채널 업무 목록 조회
     * @param {number} channelId - 채널 ID
     * @param {object} [folderScope] - { folderId? , onlyUnfiled? }
     */
    getTasks:    (channelId, folderScope = {}) =>
        api.get(`${API_BASE}/channel/${channelId}/tasks`, folderParams(folderScope)),

    /** 업무 폴더 이동 — folderId 가 null 이면 미분류로 뺀다 */
    moveTaskToFolder: (taskId, folderId) =>
        api.patch(`${API_BASE}/channel/task/${taskId}/folder`, { folderId: folderId ?? null }),

    /** 채널 업무 진행률 조회
     * @param {number} channelId - 채널 ID
     */
    getProgress: (channelId)       => api.get(`${API_BASE}/channel/${channelId}/tasks/progress`),

    /** 업무 생성
     * @param {number} channelId - 채널 ID
     * @param {object} data - 업무 데이터
     */
    createTask:  (channelId, data) => api.post(`${API_BASE}/channel/${channelId}/task`, data),

    /** 업무 수정
     * @param {number} taskId - 업무 ID
     * @param {object} data - 수정할 업무 데이터
     */
    updateTask:  (taskId, data)    => api.put(`${API_BASE}/channel/task/${taskId}`, data),

    /** 업무 상태 변경 (검토요청 워크플로우)
     *  지시자: 모든 상태 / 담당자: 시작·진행중·검토요청 (완료는 지시자만)
     * @param {number} taskId - 업무 ID
     * @param {string} status - TaskStatus (STARTED/IN_PROGRESS/REVIEW_REQUEST/DONE)
     */
    changeTaskStatus: (taskId, status) => api.patch(`${API_BASE}/channel/task/${taskId}/status`, { status }),

    /** 업무 삭제
     * @param {number} taskId - 업무 ID
     */
    deleteTask:  (taskId)          => api.delete(`${API_BASE}/channel/task/${taskId}`),

    // 업무 피드백(코멘트)
    /** 업무 피드백 목록 조회
     * @param {number} taskId - 업무 ID
     */
    getTaskComments: (taskId)          => api.get(`${API_BASE}/channel/task/${taskId}/comments`),

    /** 업무 피드백 작성 (지시자·담당자 양방향)
     * @param {number} taskId - 업무 ID
     * @param {string} content - 피드백 내용
     */
    addTaskComment:  (taskId, content) => api.post(`${API_BASE}/channel/task/${taskId}/comment`, { content }),

    /** 업무 피드백 삭제 (작성자 본인 또는 지시자)
     * @param {number} commentId - 피드백 ID
     */
    deleteTaskComment: (commentId)     => api.delete(`${API_BASE}/channel/task-comment/${commentId}`),

    // 일정
    /** 채널 일정 목록 조회
     * @param {number} channelId - 채널 ID
     * @param {object} [folderScope] - { folderId? , onlyUnfiled? }
     */
    getSchedules:   (channelId, folderScope = {}) =>
        api.get(`${API_BASE}/channel/${channelId}/schedules`, folderParams(folderScope)),

    /** 일정 폴더 이동 — folderId 가 null 이면 미분류로 뺀다 */
    moveScheduleToFolder: (scheduleId, folderId) =>
        api.patch(`${API_BASE}/channel/schedule/${scheduleId}/folder`, { folderId: folderId ?? null }),

    /** 채널 일정 생성
     * @param {number} channelId - 채널 ID
     * @param {object} data - 일정 데이터
     */
    createSchedule: (channelId, data) => api.post(`${API_BASE}/channel/${channelId}/schedule`, data),

    /** 채널 일정 수정 — 작성자 본인 또는 채널 관리자만(삭제와 같은 규칙). 폴더 이동은 moveScheduleToFolder.
     * @param {number} scheduleId - 일정 ID
     * @param {object} data - 제목·설명·기간·업무 연동
     */
    updateSchedule: (scheduleId, data) => api.put(`${API_BASE}/channel/schedule/${scheduleId}`, data),

    /** 채널 일정 삭제
     * @param {number} scheduleId - 일정 ID
     */
    deleteSchedule: (scheduleId)      => api.delete(`${API_BASE}/channel/schedule/${scheduleId}`),

    // 투표
    /** 투표 생성
     * @param {number} channelId - 채널 ID
     * @param {object} data - 투표 데이터
     */
    createPoll: (channelId, data) => api.post(`${API_BASE}/channel/${channelId}/poll`, data),

    /** 투표 참여
     * @param {number} pollId - 투표 ID
     * @param {object} data - 선택 항목 데이터
     */
    vote:       (pollId, data)    => api.post(`${API_BASE}/channel/poll/${pollId}/vote`, data),

    /** 투표 취소
     * @param {number} pollId - 투표 ID
     */
    cancelVote: (pollId)          => api.delete(`${API_BASE}/channel/poll/${pollId}/vote`),

    /** 투표 종료
     * @param {number} pollId - 투표 ID
     */
    closePoll:  (pollId)          => api.post(`${API_BASE}/channel/poll/${pollId}/close`, {}),
};
